<script setup lang="ts">
import {computed, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import useSwr from "@/core/composables/swr/useSwr";
import {teamApi} from "@/container";
import LegacyAccess from "@/core/interfaces/Access";
import {TeamTask, TeamTaskStatus} from "@/core/interfaces/TeamTask";
import {TeamTaskStoreRequest} from "@/core/http/resources/TeamTaskResource";
import useTeamTasks from "@/team-task/composables/useTeamTasks";
import {TASK_PRIORITIES} from "@/team-task/taskBoard";
import TeamTaskBoard from "@/team-task/components/TeamTaskBoard.vue";
import TeamTaskDialog from "@/team-task/components/TeamTaskDialog.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import {storeToRefs} from "pinia";
import {useSystemStore} from "@/stores/system.store";
import {useToastStore} from "@/stores/toast.store";
import {Archive, ChevronDown, CircleAlert, Plus, Undo2} from "@lucide/vue";
import dayjs from "dayjs";
import "dayjs/locale/fr";

type FilterKey = 'all' | 'mine' | 'unassigned' | TeamTask['priority'];

const {
    tasks,
    archivedTasks,
    loading,
    loadFailed,
    failure,
    clearFailure,
    create,
    update,
    move,
    reorder,
    archiveCompleted,
    restore,
    countComment
} = useTeamTasks();

const {showSuccess} = useToastStore();

const {data: teamData} = useSwr('access/current/team', () => teamApi().index());
const members = computed<LegacyAccess[]>(() => teamData.value?.team ?? []);

const {environment} = storeToRefs(useSystemStore());

/** The signed-in access point: what "Mes tâches" and "Se l'assigner" act on. */
const currentAccessId = computed<number | null>(() => environment.value?.id ?? null);

const filter = ref<FilterKey>('all');
const dialogOpen = ref<boolean>(false);
const editedTask = ref<TeamTask | null>(null);
const saving = ref<boolean>(false);
const archivesOpen = ref<boolean>(false);

const route = useRoute();
const router = useRouter();

/**
 * Deep link from the day card of the agenda: `?task=id` opens that task.
 * Watched on the list rather than read once, because the board is usually
 * still loading when the route lands. The query is dropped afterwards so a
 * refresh does not reopen what the pharmacist just closed.
 */
watch(tasks, list => {
    const wanted = Number(route.query.task);

    if (!wanted) {
        return;
    }

    const found = list.find(task => task.id === wanted);

    if (found) {
        editedTask.value = found;
        dialogOpen.value = true;
        router.replace({path: route.path});
    }
}, {immediate: true});

/**
 * Filtered here rather than server-side: the board already holds every open
 * task, so a refetch per chip would only add latency.
 */
const visibleTasks = computed(() => {
    switch (filter.value) {
        case 'mine':
            return tasks.value.filter(task =>
                currentAccessId.value !== null && task.assignee_ids.includes(currentAccessId.value)
            );
        case 'unassigned':
            return tasks.value.filter(task => !task.assignee_ids.length);
        case 'all':
            return tasks.value;
        default:
            return tasks.value.filter(task => task.priority === filter.value);
    }
});

const filters = computed<{ key: FilterKey; label: string; count: number }[]>(() => [
    {key: 'all', label: 'Toutes', count: tasks.value.length},
    {
        key: 'mine',
        label: 'Mes tâches',
        count: tasks.value.filter(task =>
            currentAccessId.value !== null && task.assignee_ids.includes(currentAccessId.value)
        ).length
    },
    {
        key: 'unassigned',
        label: 'Non assignées',
        count: tasks.value.filter(task => !task.assignee_ids.length).length
    },
    ...TASK_PRIORITIES.map(option => ({
        key: option.value as FilterKey,
        label: option.label,
        count: tasks.value.filter(task => task.priority === option.value).length
    }))
]);

const openCreation = () => {
    editedTask.value = null;
    dialogOpen.value = true;
};

const openDetail = (task: TeamTask) => {
    editedTask.value = task;
    dialogOpen.value = true;
};

const submit = async (payload: TeamTaskStoreRequest) => {
    const existing = editedTask.value;

    saving.value = true;

    const saved = existing ? await update(existing, payload) : await create(payload);

    // Released whatever the outcome: on failure the dialog stays open so the
    // pharmacist can retry without losing what was typed.
    saving.value = false;

    if (!saved) {
        return;
    }

    if (existing) {
        // Handing back the stored task drops the dialog into its reading view,
        // showing the saved state rather than closing on the pharmacist.
        editedTask.value = saved;
    } else {
        dialogOpen.value = false;
    }
};

/** "Se l'assigner": the one-click path of an unassigned card. */
const claim = (task: TeamTask) => {
    if (currentAccessId.value === null) {
        return;
    }

    update(task, {assignees: [...task.assignee_ids, currentAccessId.value]});
};

const moveTask = (payload: { task: TeamTask; status: TeamTaskStatus }) => {
    move(payload.task, payload.status);
};

const reorderColumn = (payload: { status: TeamTaskStatus; taskIds: number[] }) => {
    reorder(payload.status, payload.taskIds);
};

const archiveDone = async () => {
    const count = await archiveCompleted();

    if (count !== null) {
        showSuccess({
            content: count === 1 ? '1 tâche archivée.' : `${count} tâches archivées.`
        });
    }
};

/**
 * Bumped locally rather than refetched: the thread already knows a comment
 * landed, and the board only needs its counter to follow.
 */
const bumpCommentCount = () => {
    if (editedTask.value) {
        countComment(editedTask.value.id);
    }
};

const archivedOn = (task: TeamTask): string =>
    task.archived_at ? dayjs(task.archived_at).locale('fr').format('D MMMM YYYY') : '';

/** A filtered column shows only part of its cards, so it cannot be reordered. */
const draggable = computed(() => filter.value === 'all');
</script>

<template>
  <div class="p-4">
    <div class="mb-3.5 flex flex-wrap items-center gap-2.5">
      <h1 class="text-sm font-semibold">Tâches de l'équipe</h1>

      <div class="ml-auto flex items-center gap-2">
        <button
          class="flex h-8 items-center gap-1.5 rounded-lg border bg-white px-3 text-xs font-semibold transition"
          :class="archivesOpen
            ? 'border-primary text-primary'
            : 'border-gray-200 text-gray-700 hover:border-gray-300'"
          @click="archivesOpen = !archivesOpen"
        >
          <Archive class="size-3.5" />
          Archives ({{ archivedTasks.length }})
          <ChevronDown class="size-3 transition" :class="{'rotate-180': archivesOpen}" />
        </button>
        <button
          class="flex h-8 items-center gap-1.5 rounded-lg bg-primary px-3 text-xs font-semibold text-white"
          @click="openCreation"
        >
          <Plus class="size-3.5" />
          Nouvelle tâche
        </button>
      </div>
    </div>

    <div class="mb-3 flex gap-1.5 overflow-x-auto pb-1">
      <button
        v-for="chip in filters"
        :key="chip.key"
        class="flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition"
        :class="filter === chip.key
          ? 'border-primary bg-primary/5 text-primary'
          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'"
        @click="filter = chip.key"
      >
        {{ chip.label }}
        <span class="text-[10px] opacity-60">{{ chip.count }}</span>
      </button>
    </div>

    <div
      v-if="failure"
      class="mb-3 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] text-rose-700"
    >
      <CircleAlert class="mt-0.5 size-3.5 shrink-0" />
      <p class="flex-1">{{ failure }}</p>
      <button class="font-semibold underline" @click="clearFailure">Fermer</button>
    </div>

    <div
      v-else-if="loadFailed"
      class="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800"
    >
      Les tâches n'ont pas pu être chargées.
    </div>

    <section v-if="archivesOpen" class="mb-3 rounded-xl border border-gray-200 bg-white p-3">
      <header class="mb-2 flex items-baseline gap-2">
        <h2 class="text-xs font-semibold text-gray-700">Tâches archivées</h2>
        <span class="text-[10px] text-gray-400">12 derniers mois</span>
      </header>

      <p v-if="!archivedTasks.length" class="py-3 text-center text-[11px] text-gray-400">
        Aucune tâche archivée sur cette période.
      </p>

      <ul v-else class="divide-y divide-gray-100">
        <li v-for="task in archivedTasks" :key="task.id" class="flex items-center gap-2 py-1.5">
          <span class="flex-1 truncate text-xs text-gray-700">{{ task.title }}</span>
          <span class="shrink-0 text-[10px] text-gray-400">{{ archivedOn(task) }}</span>
          <button
            class="flex shrink-0 items-center gap-1 rounded-md border border-gray-200 px-1.5 py-0.5 text-[10px] font-semibold text-primary hover:border-primary/40"
            @click="restore(task)"
          >
            <Undo2 class="size-3" />
            Restaurer
          </button>
        </li>
      </ul>
    </section>

    <BaseFlexSpinner v-if="loading && !tasks.length" />

    <TeamTaskBoard
      v-else
      :tasks="visibleTasks"
      :members="members"
      :draggable="draggable"
      @open="openDetail"
      @claim="claim"
      @move="moveTask"
      @reorder="reorderColumn"
      @archive-completed="archiveDone"
    />

    <p class="mt-3 text-center text-[11px] text-gray-400">
      <template v-if="draggable">
        Glissez-déposez une carte entre les colonnes ou à l'intérieur de l'une d'elles pour la
        classer, ou cliquez-la pour voir le détail.
      </template>
      <template v-else>
        Le classement par glisser-déposer est indisponible tant qu'un filtre masque une partie
        des cartes. Les chevrons restent utilisables.
      </template>
    </p>

    <TeamTaskDialog
      v-model:open="dialogOpen"
      :task="editedTask"
      :members="members"
      :current-access-id="currentAccessId"
      :saving="saving"
      @submit="submit"
      @commented="bumpCommentCount"
    />
  </div>
</template>
